import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Autocomplete,
  CircularProgress,
  Divider,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Flip,
  CheckCircle,
  History,
  Business,
  Star,
  VerifiedUser,
  Shield,
  Security,
  Add,
  Cancel,
} from '@mui/icons-material';
import UserService from '../../../services/userService';
import CreditService from '../../../services/creditService';
import { toast } from 'react-toastify';

const CheckImage = forwardRef(({ 
  checkData = {},
  imageUrl = null,
  preview = false,
  showTemplate = false,
  onTransferApprove = null,
  enableTransfer = false,
  creditId = null,
  currentUserId = null,
  currentSellerId = null,
  currentBuyerId = null, // Alıcıyı filtrelemek için
  showTransferHistory = false, // Buyer için transfer geçmişini göstermek için
  isFlipped: externalIsFlipped = null, // Dışarıdan kontrol edilebilir
  onFlipChange = null, // Flip değişikliği callback
  onSelectedCustomerChange = null, // Seçilen müşteri değiştiğinde çağrılır
  myPayments = [], // Payments'teki approved payment'lar (seller seçimi için)
  receivedAmount = null, // Partial transfer alıcısı için alınan tutar
}, ref) => {
  const [internalIsFlipped, setInternalIsFlipped] = useState(false);
  const isFlipped = externalIsFlipped !== null ? externalIsFlipped : internalIsFlipped;
  const [selectedCustomers, setSelectedCustomers] = useState([]); // Array of {customer, amount}
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [backContentHeight, setBackContentHeight] = useState(null);
  const backContentRef = useRef(null);

  // Load customers list - Tüm aktif kullanıcılardan (admin ve mevcut kullanıcı hariç)
  useEffect(() => {
    if (isFlipped && enableTransfer && !showTransferHistory) {
      const loadCustomers = async () => {
        setLoadingCustomers(true);
        try {
          // Tüm aktif kullanıcıları yükle (admin ve mevcut kullanıcı hariç)
          const result = await UserService.getActiveNonAdminUsers();
          if (result.success) {
            // Filter out current seller, current user, and buyer
            const sellers = (result.data || []).filter(seller => 
              seller.sellerId !== currentSellerId && 
              seller.sellerId !== currentUserId &&
              seller.sellerId !== currentBuyerId
            );
            // Map to match NewCredit format
            const mapped = sellers.map(seller => ({
              sellerId: seller.sellerId,
              sellerName: seller.sellerName,
              userId: seller.sellerId, // For compatibility
              label: `${seller.sellerName} (ID: ${seller.sellerId})`,
              email: seller.email,
              companyName: seller.companyName,
            }));
            setCustomers(mapped);
            console.log('Loaded customers from all users:', mapped.length);
          } else {
            console.error('Failed to load sellers:', result);
          }
        } catch (error) {
          console.error('Error loading customers:', error);
        } finally {
          setLoadingCustomers(false);
        }
      };
      loadCustomers();
    } else if (!isFlipped || !enableTransfer) {
      // Reset customers when not needed
      setCustomers([]);
      setSelectedCustomers([]);
    }
  }, [isFlipped, enableTransfer, currentSellerId, currentUserId, currentBuyerId, showTransferHistory]);

  // Load transfer history (for both seller and buyer, and for check front display)
  useEffect(() => {
    if (creditId) {
      const loadHistory = async () => {
        setLoadingHistory(true);
        try {
          const result = await CreditService.getTransferHistory(creditId);
          if (result.success && result.data) {
            setTransferHistory(result.data);
          }
        } catch (error) {
          console.error('Error loading transfer history:', error);
        } finally {
          setLoadingHistory(false);
        }
      };
      loadHistory();
    }
  }, [creditId]);

  // Measure back content height when flipped or content changes
  useEffect(() => {
    if (isFlipped && backContentRef.current) {
      const updateHeight = () => {
        const height = backContentRef.current?.scrollHeight;
        if (height) {
          // Add padding: top (20px) + bottom padding (24px) + header (60px) + security bars (8px)
          const padding = 20 + 24 + 60 + 8;
          // Minimum height should match front side aspect ratio
          const minHeight = typeof window !== 'undefined' 
            ? (backContentRef.current.offsetWidth / 8.5) * 3.5 
            : 300;
          setBackContentHeight(Math.max(height + padding, minHeight));
        }
      };
      
      const timeoutId = setTimeout(updateHeight, 100);
      const resizeObserver = new ResizeObserver(() => {
        setTimeout(updateHeight, 50);
      });
      
      if (backContentRef.current) {
        resizeObserver.observe(backContentRef.current);
      }
      
      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
      };
    } else {
      setBackContentHeight(null);
    }
  }, [isFlipped, transferHistory, selectedCustomers]);

  const handleApprove = async () => {
    const validCustomers = selectedCustomers.filter(sc => sc.customer);
    if (!validCustomers || validCustomers.length === 0 || !creditId) {
      return;
    }

    setTransferring(true);
    try {
      // Parçalı transfer için PartialTransfers listesi oluştur
      const partialTransfers = validCustomers.map(sc => ({
        sellerId: sc.customer.sellerId || sc.customer.userId,
        amount: parseFloat(sc.amount) || 0
      }));

      // Her bir tutar > 0 olmalı
      const invalidAmount = partialTransfers.some(pt => !pt.amount || pt.amount <= 0);
      if (invalidAmount) {
        toast.error('Transfer amount must be greater than 0');
        setTransferring(false);
        return;
      }

      // Toplam tutarı kontrol et (transferable amount ile karşılaştır)
      const totalAmount = partialTransfers.reduce((sum, pt) => sum + pt.amount, 0);
      if (totalAmount <= 0) {
        toast.error('Total transfer amount must be greater than 0');
        setTransferring(false);
        return;
      }
      if (totalAmount > transferableAmount) {
        toast.error(`Total transfer amount (${formatCurrency(totalAmount, currency)}) cannot exceed transferable amount (${formatCurrency(transferableAmount, currency)})`);
        setTransferring(false);
        return;
      }

      const sellerNames = validCustomers
        .map(sc => sc.customer.sellerName || sc.customer.label)
        .join(', ');
      
      // Notes string'ini oluştur (max 1000 karakter)
      const notesText = `Partial transfer to ${sellerNames}`;
      const sanitizedNotes = notesText.length > 1000 ? notesText.substring(0, 1000) : notesText;
      
      // Debug: Gönderilen veriyi logla
      const transferData = {
        creditId: creditId,
        partialTransfers: partialTransfers,
        notes: sanitizedNotes,
      };
      console.log('[CheckImage] Transfer data being sent:', JSON.stringify(transferData, null, 2));
      console.log('[CheckImage] Notes text:', sanitizedNotes);
      console.log('[CheckImage] Notes contains $:', sanitizedNotes.includes('$'));
      
      const result = await CreditService.transferCredit(transferData);
      
      // Debug: Sonucu logla
      console.log('[CheckImage] Transfer result:', result);
      if (!result.success) {
        console.error('[CheckImage] Transfer failed with error:', {
          message: result.message,
          error: result.error,
          innerException: result.innerException,
          stackTrace: result.stackTrace
        });
      }

      if (result.success) {
        if (onTransferApprove) {
          onTransferApprove(result.data);
        }
        // Reload transfer history
        const historyResult = await CreditService.getTransferHistory(creditId);
        if (historyResult.success && historyResult.data) {
          setTransferHistory(historyResult.data);
        }
        // Reset form
        setSelectedCustomers([]);
        if (onSelectedCustomerChange) {
          onSelectedCustomerChange(null);
        }
        if (externalIsFlipped === null) {
          setInternalIsFlipped(false);
        }
        if (onFlipChange) {
          onFlipChange(false);
        }
      }
    } catch (error) {
      console.error('Transfer error:', error);
    } finally {
      setTransferring(false);
    }
  };

  // Expose approve method to parent component via ref
  useImperativeHandle(ref, () => ({
    approve: handleApprove,
  }));

  // Eğer imageUrl varsa ve showTemplate false ise, direkt görüntüyü göster
  if (imageUrl && !showTemplate) {
    return (
      <Paper
        elevation={8}
        sx={{
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
          border: '2px solid #e0e0e0',
          maxWidth: 800,
          mx: 'auto',
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt="Check Image"
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        />
      </Paper>
    );
  }

  // Çek template'i - Modern profesyonel tasarım
  const {
    amount = 0,
    currency = 'TRY',
    buyerName = 'Buyer Name',
    sellerName = 'Seller Name',
    buyerIban = 'TR00 0000 0000 0000 0000 0000 00',
    dueDate = new Date(),
    checkNumber = '0023',
    paymentNumber = '',
    isGuaranteed = false,
    description = '',
    creatorName = '',
  } = checkData;

  // Get original seller name from transfer history (for check front display)
  const getOriginalSellerName = () => {
    if (transferHistory.length > 0) {
      // Get the oldest transfer's previousSellerName (original seller)
      const sortedTransfers = [...transferHistory].sort((a, b) => new Date(a.transferDate) - new Date(b.transferDate));
      return sortedTransfers[0].previousSellerName || sellerName;
    }
    return sellerName;
  };

  const displaySellerName = getOriginalSellerName();

  // Display amount: Backend'den gelen amount zaten orijinal tutar (originalAmount veya amount)
  // Transfer geçmişinden hesaplamaya gerek yok, backend'den doğru değer geliyor
  const displayAmount = amount;

  // Calculate transferable amount (remaining amount after previous transfers)
  // Eğer bu bir partial transfer ise (receivedAmount varsa), o zaman sadece kendisine transfer edilen tutarı kullan
  // Değilse, orijinal seller ise, kalan tutarı hesapla
  const getTransferableAmount = () => {
    // Eğer receivedAmount varsa (partial transfer alıcısı), o zaman sadece kendisine transfer edilen tutarı kullan
    if (receivedAmount !== null && receivedAmount !== undefined) {
      // Bu seller'ın daha sonra yaptığı transfer'ları çıkar
      let transferredByThisSeller = 0;
      transferHistory.forEach(transfer => {
        if (transfer.partialTransfers && transfer.partialTransfers.length > 0) {
          // Bu transfer'in previousSellerId'si currentSellerId ise, bu seller transfer yapmış
          if (transfer.previousSellerId === currentSellerId) {
            transfer.partialTransfers.forEach(pt => {
              transferredByThisSeller += parseFloat(pt.amount) || 0;
            });
          }
        } else if (transfer.previousSellerId === currentSellerId) {
          // Partial transfer değilse, tüm tutar transfer edilmiş
          transferredByThisSeller = receivedAmount;
        }
      });
      
      // Transferable amount = alınan tutar - bu seller'ın transfer ettiği tutar
      return Math.max(0, receivedAmount - transferredByThisSeller);
    }
    
    // Eğer orijinal seller ise (partial transfer alıcısı değilse)
    // Backend'den gelen amount zaten kalan tutar (c.Amount)
    // Ama çekin ön yüzünde orijinal tutarı gösteriyoruz, bu yüzden transferable amount'u hesaplamalıyız
    if (transferHistory.length > 0) {
      let totalTransferred = 0;
      transferHistory.forEach(transfer => {
        if (transfer.partialTransfers && transfer.partialTransfers.length > 0) {
          transfer.partialTransfers.forEach(pt => {
            totalTransferred += parseFloat(pt.amount) || 0;
          });
        }
      });
      // Orijinal amount (displayAmount) - tüm transfer edilen tutarlar
      return Math.max(0, amount - totalTransferred);
    }
    
    return amount; // Transfer yoksa tüm amount transferable
  };

  const transferableAmount = getTransferableAmount();

  const formatIban = (iban) => {
    if (!iban) return '';
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    return cleanIban.match(/.{1,4}/g)?.join(' ') || cleanIban;
  };

  const formatCurrency = (value, curr) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date) => {
    if (!date) return { day: '00', month: '00', year: '0000' };
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear());
    return { day, month, year };
  };

  const dateParts = formatDate(dueDate);

  const formatAmountWords = (value, curr) => {
    const wholePart = Math.floor(value);
    const decimalPart = Math.round((value - wholePart) * 100);
    
    const currencyNames = {
      TRY: 'Turkish Lira',
      USD: 'Dollars',
      EUR: 'Euros',
      GBP: 'Pounds',
    };
    
    const formattedDecimal = String(decimalPart).padStart(2, '0');
    
    return `${numberToWords(wholePart)} ${currencyNames[curr] || 'Currency'} and ${formattedDecimal}/100`;
  };

  const getCurrencySymbol = (curr) => {
    const symbols = {
      TRY: '₺',
      USD: '$',
      EUR: '€',
      GBP: '£',
    };
    return symbols[curr] || curr;
  };

  const getCurrencyName = (curr) => {
    const names = {
      TRY: 'LIRA',
      USD: 'DOLLARS',
      EUR: 'EUROS',
      GBP: 'POUNDS',
    };
    return names[curr] || curr;
  };

  const handleFlip = () => {
    const newFlippedState = !isFlipped;
    if (externalIsFlipped === null) {
      setInternalIsFlipped(newFlippedState);
    }
    if (onFlipChange) {
      onFlipChange(newFlippedState);
    }
    if (!newFlippedState) {
      setSelectedCustomers([]);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: 700, md: 800 },
        mx: 'auto',
        position: 'relative',
        perspective: '1000px',
      }}
    >
      {/* Transfer To Autocomplete - Çekin Dışında, Üstünde (Sadece Seller için) */}
      {enableTransfer && !showTransferHistory && isFlipped && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: '#ffffff',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 600,
              color: '#2c3e50',
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Business sx={{ fontSize: 20 }} />
            Partial Transfer
          </Typography>
          
          {/* Selected Customers List */}
          {selectedCustomers.map((sc, index) => {
            const availableCustomers = customers.filter(c => 
              !selectedCustomers.some((sel, idx) => idx !== index && sel.customer && sel.customer.sellerId === c.sellerId)
            );
            
            return (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    freeSolo
                    options={availableCustomers}
                    loading={loadingCustomers}
                    value={sc.customer}
                    onChange={(event, newValue, reason) => {
                      if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
                        const customer = {
                          userId: newValue.sellerId,
                          sellerId: newValue.sellerId,
                          label: newValue.sellerName,
                          sellerName: newValue.sellerName,
                          email: newValue.email,
                          companyName: newValue.companyName,
                        };
                        
                        // Amount'ı boş bırak, kullanıcı manuel girecek
                        const updated = [...selectedCustomers];
                        updated[index] = { 
                          customer, 
                          amount: sc.amount || ''
                        };
                        setSelectedCustomers(updated);
                        if (onSelectedCustomerChange) {
                          onSelectedCustomerChange(updated);
                        }
                      } else if (reason === 'clear') {
                        const updated = selectedCustomers.filter((_, idx) => idx !== index);
                        setSelectedCustomers(updated);
                        if (onSelectedCustomerChange) {
                          onSelectedCustomerChange(updated);
                        }
                      }
                    }}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      if (typeof option === 'string') return option;
                      return `${option.sellerName} (ID: ${option.sellerId})`;
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof option === 'string' || typeof value === 'string') return false;
                      return option.sellerId === value.sellerId;
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const searchTerm = inputValue.toLowerCase();
                      return options.filter(option =>
                        option.sellerName.toLowerCase().includes(searchTerm) ||
                        option.sellerId.toString().includes(searchTerm)
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seller"
                        placeholder="Select seller"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business color="primary" fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ flex: 1 }}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    type="number"
                    label="Amount"
                    value={sc.amount || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const updated = [...selectedCustomers];
                      updated[index] = { customer: sc.customer, amount: value };
                      setSelectedCustomers(updated);
                      if (onSelectedCustomerChange) {
                        onSelectedCustomerChange(updated);
                      }
                    }}
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01,
                    }}
                    sx={{ 
                      width: 150,
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      const updated = selectedCustomers.filter((_, idx) => idx !== index);
                      setSelectedCustomers(updated);
                      if (onSelectedCustomerChange) {
                        onSelectedCustomerChange(updated);
                      }
                    }}
                    color="error"
                    size="small"
                  >
                    <Cancel />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
          
          {/* Add Seller Button */}
          <Button
            startIcon={<Add />}
            onClick={() => {
              const newCustomer = { customer: null, amount: 0 };
              setSelectedCustomers([...selectedCustomers, newCustomer]);
            }}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          >
            Add Seller
          </Button>
          
          {/* Total Amount Display */}
          {selectedCustomers.filter(sc => sc.customer).length > 0 && (
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Credit Amount:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getCurrencySymbol(currency)}{formatCurrency(amount, currency)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Transferable Amount:
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {getCurrencySymbol(currency)}{formatCurrency(transferableAmount, currency)}
                </Typography>
              </Box>
              {(() => {
                const total = selectedCustomers
                  .filter(sc => sc.customer)
                  .reduce((sum, sc) => sum + (parseFloat(sc.amount) || 0), 0);
                const remaining = transferableAmount - total;
                if (remaining > 0) {
                  return (
                    <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                      Remaining: {getCurrencySymbol(currency)}{formatCurrency(remaining, currency)} will stay with you
                    </Typography>
                  );
                } else if (remaining < 0) {
                  return (
                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                      Total exceeds transferable amount by {getCurrencySymbol(currency)}{formatCurrency(Math.abs(remaining), currency)}
                    </Typography>
                  );
                }
                return null;
              })()}
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: isFlipped && backContentHeight ? undefined : '8.5 / 3.5',
          minHeight: isFlipped && backContentHeight ? `${backContentHeight}px` : undefined,
          height: isFlipped && backContentHeight ? `${backContentHeight}px` : undefined,
          transformStyle: 'preserve-3d',
          transition: 'transform 0.6s, height 0.3s ease-in-out',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front Side - Normal Check */}
        <Paper
          elevation={8}
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            backfaceVisibility: 'hidden',
        background: '#ffffff',
        overflow: 'hidden',
        fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        // Sophisticated guilloche pattern
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 2px,
              rgba(59, 130, 246, 0.03) 2px,
              rgba(59, 130, 246, 0.03) 4px
            ),
            repeating-linear-gradient(
              90deg,
              transparent,
              transparent 2px,
              rgba(59, 130, 246, 0.03) 2px,
              rgba(59, 130, 246, 0.03) 4px
            ),
            radial-gradient(
              circle at 20% 50%,
              rgba(96, 165, 250, 0.08) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 50%,
              rgba(147, 197, 253, 0.08) 0%,
              transparent 50%
            )
          `,
          pointerEvents: 'none',
          zIndex: 0,
        },
        // Watermark effect
        '&::after': {
          content: '"SECURE"',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-45deg)',
          fontSize: { xs: '4rem', sm: '5rem', md: '6rem' },
          fontWeight: 900,
          color: 'rgba(59, 130, 246, 0.02)',
          letterSpacing: '0.5rem',
          pointerEvents: 'none',
          zIndex: 0,
        },
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      {/* Security Header Bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
          zIndex: 2,
        }}
      />

      {/* Micro-text security pattern - Top */}
      <Box
        sx={{
          position: 'absolute',
          top: 6,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: { xs: '0.3rem', sm: '0.35rem', md: '0.4rem' },
          color: 'rgba(59, 130, 246, 0.3)',
          letterSpacing: '0.5px',
          fontWeight: 500,
          zIndex: 0,
        }}
      >
        SECURE • PROTECTED • VERIFIED • AUTHENTIC • SECURE • PROTECTED • VERIFIED • AUTHENTIC • SECURE • PROTECTED • VERIFIED
      </Box>

      {/* Top Section - Modern Bank Logo and Name */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 12, sm: 14, md: 16 },
          left: { xs: 16, sm: 18, md: 20 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.2, md: 1.5 },
        }}
      >
        {/* Modern Logo */}
        <Box
          sx={{
            width: { xs: 32, sm: 36, md: 40 },
            height: { xs: 32, sm: 36, md: 40 },
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -2,
              right: -2,
              width: { xs: 16, sm: 18, md: 20 },
              height: { xs: 16, sm: 18, md: 20 },
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
            },
          }}
        >
          <Typography
            sx={{
              color: '#ffffff',
              fontSize: { xs: '1rem', sm: '1.15rem', md: '1.3rem' },
              fontWeight: 700,
              letterSpacing: '-1px',
            }}
          >
            DP
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
              letterSpacing: '-0.5px',
              lineHeight: 1.1,
            }}
          >
            DPG FINANCE
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#64748b',
              fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
              letterSpacing: '1px',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            Digital Payment
          </Typography>
        </Box>
      </Box>

      {/* Check Number - Modern Style */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 12, sm: 14, md: 16 },
          right: { xs: 16, sm: 18, md: 20 },
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '6px',
          padding: { xs: '6px 10px', sm: '7px 11px', md: '8px 12px' },
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.8, sm: 0.9, md: 1 },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
            fontWeight: 600,
            letterSpacing: '0.5px',
          }}
        >
          {isGuaranteed ? 'PG NO.' : 'P NO.'}
        </Typography>
        <Typography
          sx={{
            color: '#1e293b',
            fontSize: { xs: '0.85rem', sm: '0.92rem', md: '1rem' },
            fontWeight: 700,
            fontFamily: 'monospace',
            letterSpacing: '1px',
          }}
        >
          {paymentNumber || checkNumber || '000000'}
        </Typography>
      </Box>

      {/* Guarantee Seal - Altın Mühür (Sadece garantili payment'lar için) */}
      {isGuaranteed && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 12, sm: 14, md: 16 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 60, sm: 70, md: 75 },
            height: { xs: 60, sm: 70, md: 75 },
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'sealPulse 4s infinite ease-in-out',
            '@keyframes sealPulse': {
              '0%': {
                transform: 'translateX(-50%) scale(1)',
                filter: 'drop-shadow(0 4px 6px rgba(180, 140, 60, 0.3))',
              },
              '50%': {
                transform: 'translateX(-50%) scale(1.02)',
                filter: 'drop-shadow(0 6px 8px rgba(180, 140, 60, 0.5))',
              },
              '100%': {
                transform: 'translateX(-50%) scale(1)',
                filter: 'drop-shadow(0 4px 6px rgba(180, 140, 60, 0.3))',
              },
            },
          }}
        >
          {/* Outer jagged edge circle with gold gradient */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #bf953f, #fcf6ba, #b38728, #fbf5b7, #aa771c)',
              boxShadow: '0 4px 10px rgba(0,0,0,0.2), inset 0 2px 4px rgba(255,255,255,0.3)',
              border: '2px dashed #b38728', // Serrated/dashed look for seal effect
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 2,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.6)',
                pointerEvents: 'none',
              },
            }}
          >
            {/* Inner ring with icon and text */}
            <Box
              sx={{
                width: '85%',
                height: '85%',
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.6)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
              }}
            >
              <VerifiedUser
                sx={{
                  color: '#fff',
                  fontSize: { xs: 20, sm: 24, md: 26 },
                  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                  mb: 0.2,
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: '0.4rem', sm: '0.45rem', md: '0.5rem' },
                  fontWeight: 800,
                  color: '#fff',
                  textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                  letterSpacing: '0.5px',
                  lineHeight: 1,
                  mt: 0.1,
                }}
              >
                GUARANTEED
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Date - Check Number'ın altında */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 60, sm: 68, md: 78 },
          right: { xs: 16, sm: 18, md: 20 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.4, sm: 0.45, md: 0.5 },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
            fontWeight: 600,
            letterSpacing: '0.5px',
            mr: { xs: 0.4, sm: 0.45, md: 0.5 },
          }}
        >
          DATE
        </Typography>
        {/* Month - 2 kutucuk */}
        {dateParts.month.split('').map((digit, index) => (
          <Box
            key={`month-${index}`}
            sx={{
              width: { xs: 16, sm: 18, md: 20 },
              height: { xs: 16, sm: 18, md: 20 },
              border: '1px solid #cbd5e1',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#ffffff',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                color: '#1e293b',
                fontWeight: 600,
                fontFamily: 'monospace',
              }}
            >
              {digit}
            </Typography>
          </Box>
        ))}
        <Typography sx={{ color: '#cbd5e1', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' }, mx: 0.2 }}>/</Typography>
        {/* Day - 2 kutucuk */}
        {dateParts.day.split('').map((digit, index) => (
          <Box
            key={`day-${index}`}
            sx={{
              width: { xs: 16, sm: 18, md: 20 },
              height: { xs: 16, sm: 18, md: 20 },
              border: '1px solid #cbd5e1',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#ffffff',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                color: '#1e293b',
                fontWeight: 600,
                fontFamily: 'monospace',
              }}
            >
              {digit}
            </Typography>
          </Box>
        ))}
        <Typography sx={{ color: '#cbd5e1', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' }, mx: 0.2 }}>/</Typography>
        {/* Year - 4 kutucuk */}
        {dateParts.year.split('').map((digit, index) => (
          <Box
            key={`year-${index}`}
            sx={{
              width: { xs: 16, sm: 18, md: 20 },
              height: { xs: 16, sm: 18, md: 20 },
              border: '1px solid #cbd5e1',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#ffffff',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                color: '#1e293b',
                fontWeight: 600,
                fontFamily: 'monospace',
              }}
            >
              {digit}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 85, sm: 95, md: 105 },
          left: { xs: 16, sm: 18, md: 20 },
          right: { xs: 16, sm: 18, md: 20 },
          display: 'flex',
          gap: { xs: 2, sm: 2.5, md: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        {/* Left Side - Pay To Section */}
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Typography
            sx={{
              color: '#64748b',
              fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
              fontWeight: 600,
              letterSpacing: '0.5px',
              mb: { xs: 0.6, sm: 0.7, md: 0.8 },
              textTransform: 'uppercase',
            }}
          >
            Pay to the Order of
          </Typography>
          <Box
            sx={{
              borderBottom: '2px solid #1e293b',
              minHeight: { xs: 24, sm: 26, md: 28 },
              mb: { xs: 1.5, sm: 1.75, md: 2 },
              display: 'flex',
              alignItems: 'flex-end',
              pb: 0.5,
            }}
          >
            <Typography
              sx={{
                color: '#1e293b',
                fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                fontWeight: 600,
                wordBreak: 'break-word',
                lineHeight: 1.2,
              }}
            >
              {displaySellerName}
            </Typography>
          </Box>

          {/* Amount in Words */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              px: { xs: 1.2, sm: 1.35, md: 1.5 },
              py: { xs: 0.8, sm: 0.9, md: 1 },
              minHeight: { xs: 32, sm: 34, md: 36 },
              display: 'flex',
              alignItems: 'center',
              mb: { xs: 1.5, sm: 1.75, md: 2 },
            }}
          >
            <Typography
              sx={{
                color: '#475569',
                fontSize: { xs: '0.68rem', sm: '0.72rem', md: '0.75rem' },
                fontWeight: 500,
                textTransform: 'capitalize',
                wordBreak: 'break-word',
                lineHeight: 1.3,
              }}
            >
              {formatAmountWords(displayAmount, currency)}
            </Typography>
          </Box>
        </Box>

        {/* Right Side - Amount */}
        <Box
          sx={{
            width: { xs: '100%', sm: '160px', md: '180px' },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.2, sm: 1.35, md: 1.5 },
          }}
        >
          {/* Currency Symbol and Amount */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.7, sm: 0.75, md: 0.8 },
            }}
          >
            <Box
              sx={{
                width: { xs: 20, sm: 22, md: 24 },
                height: { xs: 20, sm: 22, md: 24 },
                border: '1.5px solid #334155',
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#ffffff',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  color: '#1e293b',
                  fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                }}
              >
                {getCurrencySymbol(currency)}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                borderBottom: '1.5px dotted #334155',
                minHeight: { xs: 24, sm: 26, md: 28 },
                display: 'flex',
                alignItems: 'flex-end',
                pb: 0.3,
              }}
            >
              <Typography
                sx={{
                  color: '#1e293b',
                  fontSize: { xs: '0.95rem', sm: '1.02rem', md: '1.1rem' },
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                  width: '100%',
                  textAlign: 'right',
                }}
              >
                {formatCurrency(displayAmount, currency)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Bottom Section */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 52, sm: 56, md: 60 },
          left: { xs: 16, sm: 18, md: 20 },
          right: { xs: 16, sm: 18, md: 20 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        {/* Memo - Sol tarafta */}
        <Box
          sx={{
            width: { xs: '100%', sm: '30%' },
            display: 'flex',
            alignItems: 'flex-end',
            gap: { xs: 0.8, sm: 0.9, md: 1 },
          }}
        >
          <Typography
            sx={{
              color: '#64748b',
              fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            Memo
          </Typography>
          <Box
            sx={{
              borderBottom: '1px solid #cbd5e1',
              minHeight: { xs: 20, sm: 21, md: 22 },
              flex: 1,
              display: 'flex',
              alignItems: 'flex-end',
              pb: 0.5,
            }}
          >
            <Typography
              sx={{
                color: '#475569',
                fontSize: { xs: '0.68rem', sm: '0.72rem', md: '0.75rem' },
                wordBreak: 'break-word',
                lineHeight: 1.2,
              }}
            >
              {description || ''}
            </Typography>
          </Box>
        </Box>

        {/* Signature - Sağ tarafta */}
        <Box
          sx={{
            width: { xs: '100%', sm: '30%' },
            textAlign: 'right',
          }}
        >
          <Box
            sx={{
              borderBottom: '2px solid #1e293b',
              minHeight: { xs: 32, sm: 34, md: 36 },
              mb: 0.5,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              pb: 0.5,
            }}
          >
            <Typography
              sx={{
                color: '#1e293b',
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                fontWeight: 600,
                fontStyle: 'italic',
                wordBreak: 'break-word',
              }}
            >
              {buyerName || ''}
            </Typography>
          </Box>
          <Typography
            sx={{
              color: '#64748b',
              fontSize: { xs: '0.58rem', sm: '0.62rem', md: '0.65rem' },
              fontWeight: 500,
              letterSpacing: '0.3px',
            }}
          >
            Authorized Signature
          </Typography>
        </Box>
      </Box>

      {/* Transfer History Badge - Çekin ön yüzünde sol altta transfer geçmişi varsa göster */}
      {transferHistory.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 20, sm: 22, md: 24 },
            left: { xs: 16, sm: 18, md: 20 },
            zIndex: 5,
          }}
        >
          <Chip
            icon={<History sx={{ fontSize: 14 }} />}
            label={`Transferred ${transferHistory.length} time${transferHistory.length > 1 ? 's' : ''}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(251, 191, 36, 0.15)',
              color: '#92400e',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              fontWeight: 600,
              fontSize: '0.7rem',
              '& .MuiChip-icon': {
                color: '#92400e',
              },
            }}
            onClick={showTransferHistory ? handleFlip : undefined}
            style={{ cursor: showTransferHistory ? 'pointer' : 'default' }}
          />
        </Box>
      )}

      {/* IBAN - En altta ortalı - Daha aşağıda */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 20, sm: 22, md: 24 },
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: '4px',
            border: '1px solid #e2e8f0',
            px: { xs: 1.2, sm: 1.35, md: 1.5 },
            py: { xs: 0.6, sm: 0.7, md: 0.8 },
            display: 'inline-block',
          }}
        >
          <Typography
            sx={{
              color: '#1e293b',
              fontSize: { xs: '0.68rem', sm: '0.74rem', md: '0.8rem' },
              fontFamily: 'monospace',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}
          >
            {formatIban(buyerIban)}
          </Typography>
        </Box>
      </Box>

      {/* Security Footer Bar with Micro-text */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%)',
          zIndex: 2,
        }}
      />

      {/* Micro-text security pattern - Bottom */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 4,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: { xs: '0.3rem', sm: '0.35rem', md: '0.4rem' },
          color: 'rgba(59, 130, 246, 0.3)',
          letterSpacing: '0.5px',
          fontWeight: 500,
          zIndex: 0,
        }}
      >
        AUTHENTIC • VERIFIED • PROTECTED • SECURE • AUTHENTIC • VERIFIED • PROTECTED • SECURE • AUTHENTIC • VERIFIED
      </Box>

      {/* Hologram effect indicator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 36, sm: 38, md: 40 },
          right: { xs: 20, sm: 23, md: 26 },
          width: { xs: 28, sm: 30, md: 32 },
          height: { xs: 28, sm: 30, md: 32 },
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 197, 253, 0.2) 100%)',
          borderRadius: '50%',
          border: '2px solid rgba(59, 130, 246, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'inset 0 2px 4px rgba(59, 130, 246, 0.2)',
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '0.85rem', sm: '0.92rem', md: '1rem' },
          }}
        >
          ✓
        </Typography>
      </Box>
    </Paper>

         {/* Back Side - Transfer Form */}
         <Paper
           elevation={8}
           sx={{
             width: '100%',
             minHeight: '100%',
             height: isFlipped && backContentHeight ? `${backContentHeight}px` : '100%',
             position: 'absolute',
             top: 0,
             left: 0,
             backfaceVisibility: 'hidden',
             transform: 'rotateY(180deg)',
             background: '#ffffff',
             overflow: 'hidden',
             fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
             boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
             border: '1px solid #e5e7eb',
             borderRadius: '8px',
             // Sophisticated guilloche pattern (ön yüzdeki gibi)
             '&::before': {
               content: '""',
               position: 'absolute',
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               background: `
                 repeating-linear-gradient(
                   0deg,
                   transparent,
                   transparent 2px,
                   rgba(59, 130, 246, 0.03) 2px,
                   rgba(59, 130, 246, 0.03) 4px
                 ),
                 repeating-linear-gradient(
                   90deg,
                   transparent,
                   transparent 2px,
                   rgba(59, 130, 246, 0.03) 2px,
                   rgba(59, 130, 246, 0.03) 4px
                 ),
                 radial-gradient(
                   circle at 20% 50%,
                   rgba(96, 165, 250, 0.08) 0%,
                   transparent 50%
                 ),
                 radial-gradient(
                   circle at 80% 50%,
                   rgba(147, 197, 253, 0.08) 0%,
                   transparent 50%
                 )
               `,
               pointerEvents: 'none',
               zIndex: 0,
             },
             '& > *': {
               position: 'relative',
               zIndex: 1,
             },
         }}
       >
       {/* Security Header Bar */}
       <Box
         sx={{
           position: 'absolute',
           top: 0,
           left: 0,
           right: 0,
           height: 4,
           background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
           zIndex: 2,
         }}
       />

                  {/* Endorsement Section - Tam genişlikte (HTML'deki gibi) */}
                  <Box
                    ref={backContentRef}
                    sx={{
                      position: 'absolute',
                      top: { xs: 12, sm: 16, md: 20 },
                      left: { xs: 16, sm: 32, md: 36 },
                      right: { xs: 16, sm: 32, md: 36 },
                      bottom: 'auto',
                      zIndex: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'visible',
                      pb: { xs: 2, sm: 2.5, md: 3 },
                    }}
                  >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: { xs: 0.75, sm: 1 }, flexShrink: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 0.25,
                  fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
                }}
              >
                ENDORSEMENT / TRANSFER HISTORY
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
                }}
              >
                View the transfer history of this payment guarantee
              </Typography>
            </Box>

            {/* Scrollable Content Area */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 1,
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f5f9',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#cbd5e1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#94a3b8',
                },
              }}
            >
            {/* Buyer View - Only Show History */}
            {showTransferHistory && !enableTransfer ? (
               <>
                   {/* Transfer History */}
                       {transferHistory.length > 0 ? (
                         // Transfer geçmişini ters sırada göster (en eski en üstte, en yeni en altta)
                         [...transferHistory].reverse().map((transfer, index, reversedArray) => {
                           // For partial transfers, check each partial transfer individually
                           // For non-partial transfers, check if the entire transfer was transferred
                           const hasPartialTransfers = transfer.partialTransfers && transfer.partialTransfers.length > 0;
                           
                           return (
                             <Box key={transfer.id}>
                               {/* Main Transfer */}
                               <Box
                                 sx={{
                                   mb: { xs: 1, sm: 1.25 },
                                   position: 'relative',
                                   pl: { xs: 3.5, sm: 4 },
                                   borderLeft: '3px solid #ddd',
                                 }}
                               >
                                 <Box
                                   sx={{
                                     position: 'absolute',
                                     left: { xs: -10, sm: -10.5 },
                                     top: 0,
                                     width: { xs: 18, sm: 20 },
                                     height: { xs: 18, sm: 20 },
                                     background: '#fff',
                                     border: '3px solid #3498db',
                                     borderRadius: '50%',
                                     zIndex: 2,
                                   }}
                                 />
                                 {/* Transfer Text */}
                                 <Typography
                                   sx={{
                                     fontFamily: '"Courier Prime", monospace',
                                     fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                                     color: '#2c3e50',
                                     lineHeight: 1.3,
                                     wordWrap: 'break-word',
                                     overflowWrap: 'break-word',
                                     maxWidth: '100%',
                                   }}
                                 >
                                   Pay to the order of{' '}
                                   <strong style={{ wordBreak: 'break-word', display: 'inline-block', maxWidth: '100%' }}>
                                     {transfer.partialTransfers && transfer.partialTransfers.length > 0
                                       ? transfer.partialTransfers.map((pt, ptIndex) => {
                                           const ptIsTransferred = transferHistory.some(t => 
                                             (t.id !== transfer.id) && (
                                               t.previousSellerId === pt.newSellerId ||
                                               (t.partialTransfers && t.partialTransfers.some(p => 
                                                 p.newSellerId === pt.newSellerId
                                               ))
                                             )
                                           );
                                           const nextTransfer = transferHistory.find(t => 
                                             (t.id !== transfer.id) && (
                                               t.previousSellerId === pt.newSellerId ||
                                               (t.partialTransfers && t.partialTransfers.some(p => 
                                                 p.newSellerId === pt.newSellerId
                                               ))
                                             )
                                           );
                                           return (
                                             <span key={ptIndex} style={{ display: 'inline-block', maxWidth: '100%', wordBreak: 'break-word' }}>
                                               {pt.newSellerName}
                                               {transfer.transferredByUserName && (
                                                 <span style={{ color: '#e74c3c', fontSize: '0.55rem', marginLeft: '0.5rem', display: 'inline-block', wordBreak: 'break-word' }}>
                                                   → Transferred by {transfer.transferredByUserName}
                                                 </span>
                                               )}
                                               {ptIndex < transfer.partialTransfers.length - 1 ? ', ' : ''}
                                             </span>
                                           );
                                         })
                                       : (
                                         <>
                                           {(() => {
                                             // For non-partial transfers, check if the entire transfer was transferred
                                             const isTransferred = transferHistory.some(t => 
                                               (t.id !== transfer.id) && (
                                                 t.previousSellerId === transfer.newSellerId ||
                                                 (t.partialTransfers && t.partialTransfers.some(pt => 
                                                   pt.newSellerId === transfer.newSellerId
                                                 ))
                                               )
                                             );
                                             const nextTransfer = transferHistory.find(t => 
                                               (t.id !== transfer.id) && (
                                                 t.previousSellerId === transfer.newSellerId ||
                                                 (t.partialTransfers && t.partialTransfers.some(pt => 
                                                   pt.newSellerId === transfer.newSellerId
                                                 ))
                                               )
                                             );
                                             return (
                                               <>
                                                 <span style={{ display: 'inline-block', maxWidth: '100%', wordBreak: 'break-word' }}>
                                                   {transfer.newSellerName}
                                                 </span>
                                                 {transfer.transferredByUserName && (
                                                   <span style={{ color: '#e74c3c', fontSize: '0.65rem', marginLeft: '0.5rem', display: 'inline-block', wordBreak: 'break-word' }}>
                                                     → Transferred by {transfer.transferredByUserName}
                                                   </span>
                                                 )}
                                               </>
                                             );
                                           })()}
                                         </>
                                       )}
                                   </strong>.
                                   <br />
                                   {transfer.partialTransfers && transfer.partialTransfers.length > 1 ? (
                                     <>
                                       Partial Transfer:
                                       {transfer.partialTransfers.map((pt, ptIndex) => {
                                         const ptIsTransferred = transferHistory.some(t => 
                                           (t.id !== transfer.id) && (
                                             t.previousSellerId === pt.newSellerId ||
                                             (t.partialTransfers && t.partialTransfers.some(p => 
                                               p.newSellerId === pt.newSellerId
                                             ))
                                           )
                                         );
                                         // Find who transferred from this seller
                                         const nextTransfer = transferHistory.find(t => 
                                           (t.id !== transfer.id) && (
                                             t.previousSellerId === pt.newSellerId ||
                                             (t.partialTransfers && t.partialTransfers.some(p => 
                                               p.newSellerId === pt.newSellerId
                                             ))
                                           )
                                         );
                                         return (
                                           <span key={ptIndex} style={{ display: 'block', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                             <br />- {pt.newSellerName}: {getCurrencySymbol(currency)}{formatCurrency(pt.amount, currency)}
                                             {transfer.transferredByUserName && (
                                               <span style={{ color: '#e74c3c', fontSize: '0.55rem', marginLeft: '0.5rem', display: 'inline-block', wordBreak: 'break-word' }}>
                                                 → Transferred by {transfer.transferredByUserName}
                                               </span>
                                             )}
                                           </span>
                                         );
                                       })}
                                     </>
                                   ) : transfer.partialTransfers && transfer.partialTransfers.length === 1 ? (
                                     <>
                                       {transfer.partialTransfers.map((pt, ptIndex) => {
                                         const ptIsTransferred = transferHistory.some(t => 
                                           (t.id !== transfer.id) && (
                                             t.previousSellerId === pt.newSellerId ||
                                             (t.partialTransfers && t.partialTransfers.some(p => 
                                               p.newSellerId === pt.newSellerId
                                             ))
                                           )
                                         );
                                         const nextTransfer = transferHistory.find(t => 
                                           (t.id !== transfer.id) && (
                                             t.previousSellerId === pt.newSellerId ||
                                             (t.partialTransfers && t.partialTransfers.some(p => 
                                               p.newSellerId === pt.newSellerId
                                             ))
                                           )
                                         );
                                         return (
                                           <span key={ptIndex} style={{ display: 'block', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                             Amount: {getCurrencySymbol(currency)}{formatCurrency(pt.amount, currency)}
                                             {transfer.transferredByUserName && (
                                               <span style={{ color: '#e74c3c', fontSize: '0.55rem', marginLeft: '0.5rem', display: 'inline-block', wordBreak: 'break-word' }}>
                                                 → Transferred by {transfer.transferredByUserName}
                                               </span>
                                             )}
                                           </span>
                                         );
                                       })}
                                     </>
                                   ) : (
                                     <>
                                       {(() => {
                                         // For non-partial transfers, check if the entire transfer was transferred
                                         const isTransferred = transferHistory.some(t => 
                                           (t.id !== transfer.id) && (
                                             t.previousSellerId === transfer.newSellerId ||
                                             (t.partialTransfers && t.partialTransfers.some(pt => 
                                               pt.newSellerId === transfer.newSellerId
                                             ))
                                           )
                                         );
                                         const nextTransfer = transferHistory.find(t => 
                                           (t.id !== transfer.id) && (
                                             t.previousSellerId === transfer.newSellerId ||
                                             (t.partialTransfers && t.partialTransfers.some(pt => 
                                               pt.newSellerId === transfer.newSellerId
                                             ))
                                           )
                                         );
                                         return (
                                           <>
                                             Value received for services.
                                             {transfer.transferredByUserName && (
                                               <span style={{ color: '#e74c3c', fontSize: '0.65rem', marginLeft: '0.5rem' }}>
                                                 → Transferred by {transfer.transferredByUserName}
                                               </span>
                                             )}
                                           </>
                                         );
                                       })()}
                                     </>
                                   )}
                                 </Typography>
                                 {/* Timestamp */}
                                 <Typography
                                   sx={{
                                     fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
                                     color: '#7f8c8d',
                                     mt: 0.5,
                                     fontFamily: '"Courier Prime", monospace',
                                   }}
                                 >
                                   Date: {new Date(transfer.transferDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} | Transaction No: #{transfer.id.toString().substring(0, 8).toUpperCase()}
                                 </Typography>
                               </Box>
                             </Box>
                           );
                         })
                       ) : (
                         <Box
                           sx={{
                             textAlign: 'center',
                             py: { xs: 2, sm: 3 },
                             color: '#95a5a6',
                           }}
                         >
                           <History sx={{ fontSize: { xs: 24, sm: 32 }, mb: 1, opacity: 0.5 }} />
                           <Typography
                             sx={{
                               fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                               fontStyle: 'italic',
                             }}
                           >
                             No transfer history available
                           </Typography>
                         </Box>
                       )}
              </>
            ) : (
              <>
              
            {/* Seller View - Transfer Form */}

               {transferHistory.length > 0 ? (
                 // Transfer geçmişini ters sırada göster (en eski en üstte, en yeni en altta)
                 [...transferHistory].reverse().map((transfer, index, reversedArray) => {
                   // For partial transfers, check each partial transfer individually
                   // For non-partial transfers, check if the entire transfer was transferred
                   const hasPartialTransfers = transfer.partialTransfers && transfer.partialTransfers.length > 0;
                   
                   return (
                     <Box
                       key={transfer.id}
                       sx={{
                         mb: { xs: 1, sm: 1.25 },
                         position: 'relative',
                         pl: { xs: 3.5, sm: 4 },
                         borderLeft: '3px solid #ddd',
                       }}
                     >
                       <Box
                         sx={{
                           position: 'absolute',
                           left: { xs: -10, sm: -10.5 },
                           top: 0,
                           width: { xs: 18, sm: 20 },
                           height: { xs: 18, sm: 20 },
                           background: '#fff',
                           border: '3px solid #3498db',
                           borderRadius: '50%',
                           zIndex: 2,
                         }}
                       /> 
                       {/* Transfer Text */}
                       <Typography
                         sx={{
                           fontFamily: '"Courier Prime", monospace',
                           fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                           color: '#2c3e50',
                           lineHeight: 1.3,
                           wordWrap: 'break-word',
                           overflowWrap: 'break-word',
                           maxWidth: '100%',
                         }}
                       >
                         Pay to the order of{' '}
                         <strong style={{ wordBreak: 'break-word', display: 'inline-block', maxWidth: '100%' }}>
                           {transfer.partialTransfers && transfer.partialTransfers.length > 0
                             ? transfer.partialTransfers.map((pt, ptIndex) => {
                                 const ptIsTransferred = transferHistory.some(t => 
                                   (t.id !== transfer.id) && (
                                     t.previousSellerId === pt.newSellerId ||
                                     (t.partialTransfers && t.partialTransfers.some(p => 
                                       p.newSellerId === pt.newSellerId
                                     ))
                                   )
                                 );
                                 // Find who transferred from this seller
                                 const nextTransfer = transferHistory.find(t => 
                                   (t.id !== transfer.id) && (
                                     t.previousSellerId === pt.newSellerId ||
                                     (t.partialTransfers && t.partialTransfers.some(p => 
                                       p.newSellerId === pt.newSellerId
                                     ))
                                   )
                                 );
                                 return (
                                   <span key={ptIndex} style={{ display: 'inline-block', maxWidth: '100%', wordBreak: 'break-word' }}>
                                     {pt.newSellerName}
                                     {transfer.transferredByUserName && (
                                       <span style={{ color: '#e74c3c', fontSize: '0.65rem', marginLeft: '0.5rem', display: 'inline-block', wordBreak: 'break-word' }}>
                                         → Transferred by {transfer.transferredByUserName}
                                       </span>
                                     )}
                                     {ptIndex < transfer.partialTransfers.length - 1 ? ', ' : ''}
                                   </span>
                                 );
                               })
                             : (
                               <>
                                 {(() => {
                                   // For non-partial transfers, check if the entire transfer was transferred
                                   const isTransferred = transferHistory.some(t => 
                                     (t.id !== transfer.id) && (
                                       t.previousSellerId === transfer.newSellerId ||
                                       (t.partialTransfers && t.partialTransfers.some(pt => 
                                         pt.newSellerId === transfer.newSellerId
                                       ))
                                     )
                                   );
                                   const nextTransfer = transferHistory.find(t => 
                                     (t.id !== transfer.id) && (
                                       t.previousSellerId === transfer.newSellerId ||
                                       (t.partialTransfers && t.partialTransfers.some(pt => 
                                         pt.newSellerId === transfer.newSellerId
                                       ))
                                     )
                                   );
                                   return (
                                     <>
                                       <span style={{ display: 'inline-block', maxWidth: '100%', wordBreak: 'break-word' }}>
                                         {transfer.newSellerName}
                                       </span>
                                       {transfer.transferredByUserName && (
                                         <span style={{ color: '#e74c3c', fontSize: '0.65rem', marginLeft: '0.5rem', display: 'inline-block', wordBreak: 'break-word' }}>
                                           → Transferred by {transfer.transferredByUserName}
                                         </span>
                                       )}
                                     </>
                                   );
                                 })()}
                               </>
                             )}
                         </strong>.
                         <br />
                         {transfer.partialTransfers && transfer.partialTransfers.length > 1 ? (
                           <>
                             Partial Transfer:
                             {transfer.partialTransfers.map((pt, ptIndex) => {
                               const ptIsTransferred = transferHistory.some(t => 
                                 (t.id !== transfer.id) && (
                                   t.previousSellerId === pt.newSellerId ||
                                   (t.partialTransfers && t.partialTransfers.some(p => 
                                     p.newSellerId === pt.newSellerId
                                   ))
                                 )
                               );
                               const nextTransfer = transferHistory.find(t => 
                                 (t.id !== transfer.id) && (
                                   t.previousSellerId === pt.newSellerId ||
                                   (t.partialTransfers && t.partialTransfers.some(p => 
                                     p.newSellerId === pt.newSellerId
                                   ))
                                 )
                               );
                                         return (
                                           <span key={ptIndex} style={{ display: 'block', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                             <br />- {pt.newSellerName}: {getCurrencySymbol(currency)}{formatCurrency(pt.amount, currency)}
                                             {transfer.transferredByUserName && (
                                               <span style={{ color: '#e74c3c', fontSize: '0.55rem', marginLeft: '0.5rem', display: 'inline-block', wordBreak: 'break-word' }}>
                                                 → Transferred by {transfer.transferredByUserName}
                                               </span>
                                             )}
                                           </span>
                                         );
                             })}
                           </>
                         ) : transfer.partialTransfers && transfer.partialTransfers.length === 1 ? (
                           <>
                             {transfer.partialTransfers.map((pt, ptIndex) => {
                               const ptIsTransferred = transferHistory.some(t => 
                                 (t.id !== transfer.id) && (
                                   t.previousSellerId === pt.newSellerId ||
                                   (t.partialTransfers && t.partialTransfers.some(p => 
                                     p.newSellerId === pt.newSellerId
                                   ))
                                 )
                               );
                               const nextTransfer = transferHistory.find(t => 
                                 (t.id !== transfer.id) && (
                                   t.previousSellerId === pt.newSellerId ||
                                   (t.partialTransfers && t.partialTransfers.some(p => 
                                     p.newSellerId === pt.newSellerId
                                   ))
                                 )
                               );
                               return (
                                 <span key={ptIndex} style={{ display: 'block', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                   Amount: {getCurrencySymbol(currency)}{formatCurrency(pt.amount, currency)}
                                   {transfer.transferredByUserName && (
                                     <span style={{ color: '#e74c3c', fontSize: '0.65rem', marginLeft: '0.5rem', display: 'inline-block', wordBreak: 'break-word' }}>
                                       → Transferred by {transfer.transferredByUserName}
                                     </span>
                                   )}
                                 </span>
                               );
                             })}
                           </>
                         ) : (
                           <>
                             {(() => {
                               // For non-partial transfers, check if the entire transfer was transferred
                               const isTransferred = transferHistory.some(t => 
                                 (t.id !== transfer.id) && (
                                   t.previousSellerId === transfer.newSellerId ||
                                   (t.partialTransfers && t.partialTransfers.some(pt => 
                                     pt.newSellerId === transfer.newSellerId
                                   ))
                                 )
                               );
                               const nextTransfer = transferHistory.find(t => 
                                 (t.id !== transfer.id) && (
                                   t.previousSellerId === transfer.newSellerId ||
                                   (t.partialTransfers && t.partialTransfers.some(pt => 
                                     pt.newSellerId === transfer.newSellerId
                                   ))
                                 )
                               );
                                   return (
                                     <>
                                       <span style={{ display: 'inline-block', maxWidth: '100%', wordBreak: 'break-word' }}>
                                         Value received for services.
                                       </span>
                                       {transfer.transferredByUserName && (
                                         <span style={{ color: '#e74c3c', fontSize: '0.65rem', marginLeft: '0.5rem', display: 'inline-block', wordBreak: 'break-word' }}>
                                           → Transferred by {transfer.transferredByUserName}
                                         </span>
                                       )}
                                     </>
                                   );
                             })()}
                           </>
                         )}
                       </Typography>
                       {/* Timestamp */}
                       <Typography
                         sx={{
                           fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                           color: '#7f8c8d',
                           mt: 0.5,
                           fontFamily: '"Courier Prime", monospace',
                         }}
                       >
                         Date: {new Date(transfer.transferDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} | Transaction No: #{transfer.id.toString().substring(0, 8).toUpperCase()}
                       </Typography>
                     </Box>
                   );
                 })
               ) : selectedCustomers.length === 0 ? (
                 <Box
                   sx={{
                     textAlign: 'center',
                     py: { xs: 2, sm: 3 },
                     color: '#95a5a6',
                   }}
                 >
                   <History sx={{ fontSize: { xs: 24, sm: 32 }, mb: 1, opacity: 0.5 }} />
                   <Typography
                     sx={{
                       fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                       fontStyle: 'italic',
                     }}
                   >
                     No transfer history yet
                   </Typography>
                 </Box>
               ) : null}

                    {/* Selected Customers - Show after transfer history (at the bottom) */}
                    {selectedCustomers.map((sc, index) => (
                      sc.customer && (
                        <Box
                          key={index}
                          sx={{
                            mb: { xs: 1, sm: 1.25 },
                            position: 'relative',
                            pl: { xs: 3.5, sm: 4 },
                            borderLeft: '3px solid #3498db',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: { xs: -10, sm: -10.5 },
                              top: 0,
                              width: { xs: 18, sm: 20 },
                              height: { xs: 18, sm: 20 },
                              background: '#fff',
                              border: '3px solid #3498db',
                              borderRadius: '50%',
                              zIndex: 2,
                            }}
                          />
                          {/* Transfer Text */}
                          <Typography
                            sx={{
                              fontFamily: '"Courier Prime", monospace',
                              fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                              color: '#2c3e50',
                              lineHeight: 1.3,
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              maxWidth: '100%',
                            }}
                          >
                            Pay to the order of <strong style={{ wordBreak: 'break-word', display: 'inline-block', maxWidth: '100%' }}>{sc.customer.sellerName || sc.customer.label}</strong>.
                            <br />
                            Amount: {getCurrencySymbol(currency)}{formatCurrency(parseFloat(sc.amount) || 0, currency)}
                            <br />
                            Value received for transfer.
                          </Typography>
                        </Box>
                      )
                    ))}
              </>
            )}
            </Box>
          </Box>

          {/* Watermark effect (same as front) */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              fontSize: { xs: '4rem', sm: '5rem', md: '6rem' },
              fontWeight: 900,
              color: 'rgba(59, 130, 246, 0.02)',
              letterSpacing: '0.5rem',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          >
            SECURE
          </Box>

          {/* Security Footer Bar with Micro-text */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              background: 'linear-gradient(90deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%)',
              zIndex: 2,
            }}
          />

          {/* Micro-text security pattern - Bottom */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 4,
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: { xs: '0.3rem', sm: '0.35rem', md: '0.4rem' },
              color: 'rgba(59, 130, 246, 0.3)',
              letterSpacing: '0.5px',
              fontWeight: 500,
              zIndex: 0,
            }}
          >
            AUTHENTIC • VERIFIED • PROTECTED • SECURE • AUTHENTIC • VERIFIED • PROTECTED • SECURE • AUTHENTIC • VERIFIED
          </Box>

          {/* Micro-text security pattern - Top */}
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: { xs: '0.3rem', sm: '0.35rem', md: '0.4rem' },
              color: 'rgba(59, 130, 246, 0.3)',
              letterSpacing: '0.5px',
              fontWeight: 500,
              zIndex: 0,
            }}
          >
            SECURE • PROTECTED • VERIFIED • AUTHENTIC • SECURE • PROTECTED • VERIFIED • AUTHENTIC • SECURE • PROTECTED • VERIFIED
          </Box>

          {/* Hologram effect indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 36, sm: 38, md: 40 },
              right: { xs: 20, sm: 23, md: 26 },
              width: { xs: 28, sm: 30, md: 32 },
              height: { xs: 28, sm: 30, md: 32 },
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 197, 253, 0.2) 100%)',
              borderRadius: '50%',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'inset 0 2px 4px rgba(59, 130, 246, 0.2)',
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.85rem', sm: '0.92rem', md: '1rem' },
              }}
            >
              ✓
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
});

// Helper function to convert number to words
const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  if (num < 20) {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    return ones[num];
  }
  if (num < 100) {
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + numberToWords(num % 10) : '');
  }
  if (num < 1000) {
    return numberToWords(Math.floor(num / 100)) + ' Hundred' + (num % 100 !== 0 ? ' ' + numberToWords(num % 100) : '');
  }
  if (num < 1000000) {
    return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + numberToWords(num % 1000) : '');
  }
  if (num < 1000000000) {
    return numberToWords(Math.floor(num / 1000000)) + ' Million' + (num % 1000000 !== 0 ? ' ' + numberToWords(num % 1000000) : '');
  }
  return num.toString();
};

CheckImage.displayName = 'CheckImage';

export default CheckImage;